import React from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Card,
  CardContent,
  Collapse,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import moment from 'moment';

import {
  ISectionForUser,
  SECTION_TYPES,
} from '../../../../store/V2/SectionSlice';
import { isEditorValueIsEmpty } from '../../../../components/Inputs/EditableTextWithDraftEditor';
import SubSectionItem from './SubSectionItem';
import ZoomSectionItem from './ZoomSectionItem';
import ResourceSectionItem from './ResourceSectionItem';
import MixedSectionContent from './MixedSectionContent';

interface SectionItemProps {
  openedSections: string[];
  keyName: string;
  collapse: boolean;
  orderNumber: number;
  section: ISectionForUser;
  isOneToOne: boolean;
  isCourseSubscribed?: boolean;
  toggleCollapse: (section: ISectionForUser) => void;
  onCancelClass: (section: ISectionForUser) => void;
  onRescheduleClass: (section: ISectionForUser) => void;
}

export const getCountDownColor = (date: string): string => {
  const daysDifference = moment(date).diff(moment(), 'days');

  if (daysDifference < 1) {
    return '#FF5630';
  }

  return '#22C55E';
};

export const getCompletedStatusData = (
  startDate?: string,
  endDate?: string
): { status: string; color: string } => {
  let status = '';
  let color = '#647488';

  if (
    startDate &&
    endDate &&
    moment(startDate) < moment() &&
    moment() < moment(endDate)
  ) {
    status = 'Ongoing';
    color = 'info.main';
  } else if (startDate && moment() < moment(startDate)) {
    status = 'Upcoming';
    color = 'info.main';
  } else if (startDate && moment() > moment(startDate)) {
    status = 'Completed';
  }

  return {
    status,
    color,
  };
};

export const getSectionTypeCounts = (
  section: ISectionForUser
): { [key: string]: number } => {
  const countData = Object.keys(SECTION_TYPES).reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {});

  (section.sections || []).forEach((sectionItem) => {
    if (countData[sectionItem.type]) {
      countData[sectionItem.type] += 1;
    } else {
      countData[sectionItem.type] = 1;
    }
  });

  return countData;
};

const containerStyles: SxProps = {
  backgroundColor: '#F8F9FD',
  borderRadius: '10px',
};

const SectionItem: React.FC<SectionItemProps> = ({
  openedSections,
  keyName,
  orderNumber,
  section,
  collapse,
  isOneToOne,
  isCourseSubscribed = false,
  toggleCollapse,
  onCancelClass,
  onRescheduleClass,
}) => {
  let statusData = getCompletedStatusData(section.startDate, section.endDate);
  const sectionTypesCountData = getSectionTypeCounts(section);

  let subSections = section.sections || [];
  let zoomData: ISectionForUser | undefined;
  let resourceData: ISectionForUser | undefined;

  if (sectionTypesCountData[SECTION_TYPES.ZOOM] === 1) {
    zoomData = subSections.find((item) => item.type === SECTION_TYPES.ZOOM);

    if (zoomData) {
      subSections = subSections.filter((item) => item._id !== zoomData?._id);

      statusData = getCompletedStatusData(
        zoomData.dateTime,
        moment(zoomData.dateTime)
          .add(zoomData.duration, 'minutes')
          .toISOString()
      );
    }
  }

  if (sectionTypesCountData[SECTION_TYPES.RESOURCE] === 1) {
    resourceData = subSections.find(
      (item) => item.type === SECTION_TYPES.RESOURCE
    );

    if (resourceData) {
      subSections = subSections.filter(
        (item) => item._id !== resourceData?._id
      );
    }
  }

  return (
    <Card elevation={0} sx={containerStyles}>
      <CardContent sx={{ p: '16px' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" flexGrow={1} mr="15px">
            <Avatar
              sx={{
                width: '36px',
                height: '36px',
                backgroundColor: isCourseSubscribed ? '#CBD5E1' : '#1C9FF7',
                color: '#F8FAFC',
                mr: '15px',
              }}
            >
              {orderNumber}
            </Avatar>
            <Stack>
              <div style={{ width: '100%' }}>
                <Typography
                  fontSize="16px"
                  fontWeight={600}
                  className="ellipsis-text one-line"
                >
                  {zoomData ? zoomData.title : section.title}
                </Typography>
              </div>
              {statusData.status ? (
                <div>
                  <Typography
                    fontSize="14px"
                    fontWeight={500}
                    color={statusData.color}
                  >
                    {statusData.status}
                  </Typography>
                </div>
              ) : null}
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center">
            {section.startDate && moment(section.startDate) > moment() ? (
              <Typography
                variant="subtitle2"
                style={{ minWidth: '150px', textTransform: 'capitalize' }}
                color={getCountDownColor(section.startDate)}
              >
                {moment(section.startDate).fromNow()}
              </Typography>
            ) : null}
            <ButtonBase
              onClick={() => toggleCollapse(section)}
              sx={{
                padding: '8px 6px',
                backgroundColor: '#E2E8F0',
                color: '#334155',
                borderRadius: '5px',
                fontWeight: 600,
                fontSize: '12px',
                minWidth: '72px',
              }}
            >
              {collapse ? 'HIDE' : 'VIEW'}
            </ButtonBase>
          </Stack>
        </Stack>
      </CardContent>
      <Collapse in={collapse}>
        <CardContent sx={{ p: '0 32px' }}>
          {!isEditorValueIsEmpty(section.description) ? (
            <div style={{ marginBottom: '15px' }}>
              {/* eslint-disable-next-line react/no-danger */}
              <Box
                sx={{
                  '& p': {
                    margin: 0,
                  },
                }}
                dangerouslySetInnerHTML={{ __html: section.description }}
              />
            </div>
          ) : null}
          {zoomData || resourceData ? (
            <MixedSectionContent
              zoomData={zoomData}
              resourceData={resourceData}
              isCourseSubscribed={isCourseSubscribed}
              isOneToOne={isOneToOne}
              onCancelClass={onCancelClass}
              onRescheduleClass={onRescheduleClass}
            />
          ) : null}
          <Stack spacing="8px">
            {subSections.map((item, index) => {
              const _keyName = `${keyName}-${index}`;
              const _orderNumber = index + 1;

              if (item.type === SECTION_TYPES.SECTION) {
                const _collapse = openedSections.some(
                  (itemId) => itemId === item._id
                );

                return (
                  <Stack
                    id={_keyName}
                    key={_keyName}
                    sx={{ border: '1px dashed #CBD5E1', borderRadius: '7px' }}
                  >
                    <SubSectionItem
                      isCourseSubscribed={isCourseSubscribed}
                      openedSections={openedSections}
                      parentId={section._id}
                      keyName={_keyName}
                      orderNumber={_orderNumber}
                      section={item}
                      collapse={_collapse}
                      toggleCollapse={toggleCollapse}
                      onCancelClass={onCancelClass}
                      isOneToOne={isOneToOne}
                      onRescheduleClass={onRescheduleClass}
                    />
                  </Stack>
                );
              }

              if (item.type === SECTION_TYPES.ZOOM && !zoomData) {
                return (
                  <Stack id={_keyName} key={_keyName}>
                    <ZoomSectionItem
                      section={item}
                      orderNumber={_orderNumber}
                      isOneToOne={isOneToOne}
                      onCancelClass={onCancelClass}
                      onRescheduleClass={onRescheduleClass}
                      isCourseSubscribed={isCourseSubscribed}
                    />
                  </Stack>
                );
              }

              if (item.type === SECTION_TYPES.RESOURCE && !resourceData) {
                return (
                  <Stack id={_keyName} key={_keyName}>
                    <ResourceSectionItem
                      isCourseSubscribed={isCourseSubscribed}
                      orderNumber={_orderNumber}
                      section={item}
                    />
                  </Stack>
                );
              }

              return null;
            })}
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default SectionItem;
